import React, { useEffect, useState } from 'react';
import {
    Box,
    VStack,
    HStack,
    Heading,
    Text,
    Button,
    Card,
    CardBody,
    Badge,
    IconButton,
    useDisclosure,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Input,
    useToast,
    Container,
    useColorModeValue,
    Spinner,
    Center,
    Flex
} from '@chakra-ui/react';
import { ArrowBackIcon, AddIcon, EditIcon, DeleteIcon, ChatIcon } from '@chakra-ui/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Persona, SessionMetadata } from '../types';
import personaServices from '../services/PersonaServices';
import sessionManager from '../utils/SessionManager';
import { generateSystemMessage, capitalizeFirstChar } from '../utils/systemMessageGenerator';

const SessionListPage = () => {
    const { personaId } = useParams<{ personaId: string }>();
    const [searchParams] = useSearchParams();
    const [persona, setPersona] = useState<Persona | null>(null);
    const [sessions, setSessions] = useState<SessionMetadata[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedSession, setSelectedSession] = useState<SessionMetadata | null>(null);
    const [newSessionTitle, setNewSessionTitle] = useState('');
    const navigate = useNavigate();
    const toast = useToast();

    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure();
    const cancelRef = React.useRef<HTMLButtonElement>(null);

    const cardBg = useColorModeValue('white', 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const mutedTextColor = useColorModeValue('gray.500', 'gray.400');

    useEffect(() => {
        const loadData = async () => {
            if (!personaId) return;

            try {
                setLoading(true);
                const bootstrapVersion = searchParams.get('bootstrap_version') || '3';
                const personaData = await personaServices.getPersona(personaId, bootstrapVersion);
                setPersona(personaData);
                
                const sessionData = sessionManager.getSessions(personaId);
                setSessions(sessionData);
            } catch (error) {
                console.error('Error loading data:', error);
                toast({
                    title: 'Error',
                    description: 'Failed to load persona or sessions',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [personaId, toast]);

    const formatDate = (timestamp: number): string => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return `Today ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        } else if (diffDays === 1) {
            return `Yesterday ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        } else if (diffDays < 7) {
            return `${diffDays} days ago`;
        } else {
            return date.toLocaleDateString();
        }
    };

    const handleCreateSession = () => {
        if (!personaId) return;
        
        const session = sessionManager.createSession(personaId);
        navigate(`/chat/${personaId}/${session.id}`);
    };

    const handleContinueSession = (session: SessionMetadata) => {
        sessionManager.setActiveSession(personaId!, session.id);
        navigate(`/chat/${personaId}/${session.id}`);
    };

    const handleDeleteSession = () => {
        if (!selectedSession || !personaId) return;

        sessionManager.deleteSession(personaId, selectedSession.id);
        setSessions(prev => prev.filter(s => s.id !== selectedSession.id));
        setSelectedSession(null);
        onDeleteClose();

        toast({
            title: 'Session deleted',
            status: 'info',
            duration: 2000,
            isClosable: true,
        });
    };

    const handleRenameSession = () => {
        if (!selectedSession || !personaId || !newSessionTitle.trim()) return;

        sessionManager.renameSession(personaId, selectedSession.id, newSessionTitle.trim());
        setSessions(prev => 
            prev.map(s => s.id === selectedSession.id 
                ? { ...s, title: newSessionTitle.trim() } 
                : s
            )
        );
        setSelectedSession(null);
        setNewSessionTitle('');
        onRenameClose();

        toast({
            title: 'Session renamed',
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    const openDeleteDialog = (session: SessionMetadata) => {
        setSelectedSession(session);
        onDeleteOpen();
    };

    const openRenameDialog = (session: SessionMetadata) => {
        setSelectedSession(session);
        setNewSessionTitle(session.title);
        onRenameOpen();
    };

    const getPersonaSystemMessage = (persona: Persona): string => {
        if (persona.bootstrap_version === '3') {
            return generateSystemMessage(persona);
        }
        return persona.systemMessage || '';
    };

    if (loading) {
        return (
            <Container maxW="container.lg" py={8}>
                <Center>
                    <VStack spacing={4}>
                        <Spinner size="xl" color="blue.500" />
                        <Text>Loading sessions...</Text>
                    </VStack>
                </Center>
            </Container>
        );
    }

    if (!persona) {
        return (
            <Container maxW="container.lg" py={8}>
                <Center>
                    <Text fontSize="lg" color="red.500">
                        Persona not found
                    </Text>
                </Center>
            </Container>
        );
    }

    return (
        <Container maxW="container.lg" py={8}>
            <VStack spacing={6} align="stretch">
                <HStack>
                    <IconButton
                        icon={<ArrowBackIcon />}
                        aria-label="Back to personas"
                        variant="ghost"
                        onClick={() => navigate('/')}
                    />
                    <Box flex={1}>
                        <Heading size="lg">{capitalizeFirstChar(persona.name) || 'Unnamed Persona'}</Heading>
                        <Text color={textColor} fontSize="sm">
                            {persona.provider} • {sessions.length} session{sessions.length !== 1 ? 's' : ''}
                        </Text>
                    </Box>
                    <Button
                        leftIcon={<AddIcon />}
                        colorScheme="blue"
                        onClick={handleCreateSession}
                    >
                        New Session
                    </Button>
                </HStack>

                {persona.desc && (
                    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <CardBody>
                            <Text fontSize="sm" color={textColor}>
                                {persona.desc}
                            </Text>
                        </CardBody>
                    </Card>
                )}

                {sessions.length === 0 ? (
                    <Center py={12}>
                        <VStack spacing={4}>
                            <ChatIcon boxSize={12} color={mutedTextColor} />
                            <Heading size="md" color={mutedTextColor}>
                                No conversations yet
                            </Heading>
                            <Text color={mutedTextColor} textAlign="center">
                                Start your first conversation with this persona
                            </Text>
                            <Button
                                leftIcon={<AddIcon />}
                                colorScheme="blue"
                                onClick={handleCreateSession}
                            >
                                Start Conversation
                            </Button>
                        </VStack>
                    </Center>
                ) : (
                    <VStack spacing={4} align="stretch">
                        {sessions.map((session) => (
                            <Card
                                key={session.id}
                                bg={cardBg}
                                borderColor={borderColor}
                                borderWidth="1px"
                                _hover={{
                                    borderColor: 'blue.300',
                                    transform: 'translateY(-1px)',
                                    transition: 'all 0.2s'
                                }}
                                cursor="pointer"
                                onClick={() => handleContinueSession(session)}
                            >
                                <CardBody>
                                    <Flex justify="space-between" align="start">
                                        <Box flex={1} mr={4}>
                                            <HStack justify="space-between" mb={2}>
                                                <Heading size="sm" noOfLines={1}>
                                                    {session.title}
                                                </Heading>
                                                <Badge
                                                    colorScheme="green"
                                                    variant="subtle"
                                                    fontSize="xs"
                                                >
                                                    {session.message_count} message{session.message_count !== 1 ? 's' : ''}
                                                </Badge>
                                            </HStack>
                                            
                                            {session.last_message_preview && (
                                                <Text
                                                    fontSize="sm"
                                                    color={mutedTextColor}
                                                    noOfLines={2}
                                                    mb={2}
                                                >
                                                    {session.last_message_preview}
                                                </Text>
                                            )}
                                            
                                            <Text fontSize="xs" color={mutedTextColor}>
                                                {formatDate(session.updated_at)}
                                            </Text>
                                        </Box>

                                        <HStack spacing={1}>
                                            <IconButton
                                                icon={<EditIcon />}
                                                aria-label="Rename session"
                                                size="sm"
                                                variant="ghost"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openRenameDialog(session);
                                                }}
                                            />
                                            <IconButton
                                                icon={<DeleteIcon />}
                                                aria-label="Delete session"
                                                size="sm"
                                                variant="ghost"
                                                colorScheme="red"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openDeleteDialog(session);
                                                }}
                                            />
                                        </HStack>
                                    </Flex>
                                </CardBody>
                            </Card>
                        ))}
                    </VStack>
                )}
            </VStack>

            {/* Delete Confirmation Dialog */}
            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelRef}
                onClose={onDeleteClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Session
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            Are you sure you want to delete "{selectedSession?.title}"? 
                            This action cannot be undone.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={handleDeleteSession} ml={3}>
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Rename Session Modal */}
            <Modal isOpen={isRenameOpen} onClose={onRenameClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Rename Session</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Input
                            value={newSessionTitle}
                            onChange={(e) => setNewSessionTitle(e.target.value)}
                            placeholder="Enter new session title"
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleRenameSession();
                                }
                            }}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onRenameClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="blue"
                            onClick={handleRenameSession}
                            isDisabled={!newSessionTitle.trim()}
                        >
                            Rename
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Container>
    );
};

export default SessionListPage;